import React from 'react';
import PhotoActionModal from './PhotoActionModal';

interface PhotoUploadAreaProps {
  photo: { base64: string } | null;
  onUpload: () => void;
  onWebcam: () => void;
  onIPCamera: () => void;
  onRemove: () => void;
  onZoom: (url: string, title: string, onReplace: () => void, onRemove: () => void) => void;
  shape: 'circle' | 'rectangle';
  aspectRatio?: string;
  /**
   * How the photo fills its box. 'cover' for identity and document photos, which are framed to
   * the box's ratio and look best edge-to-edge. 'contain' for a vehicle, where cropping again at
   * display time cuts the roof and the number plate off a photo the operator already framed.
   */
  fit?: 'cover' | 'contain';
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  label: string;
  required?: boolean;
  title: string;
  darkMode: boolean;
}

const PhotoUploadArea: React.FC<PhotoUploadAreaProps> = ({
  photo,
  onUpload,
  onWebcam,
  onIPCamera,
  onRemove,
  onZoom,
  shape,
  aspectRatio = 'aspect-square',
  fit = 'cover',
  icon: Icon,
  badge,
  label,
  required,
  title,
  darkMode
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [showActionModal, setShowActionModal] = React.useState(false);

  const isEmpty = !photo;

  const photoClasses = `
    relative w-full ${shape === 'circle' ? 'aspect-[4/5]' : aspectRatio} overflow-hidden
    ${shape === 'circle' ? 'rounded-xl' : 'rounded-lg'}
    ${isEmpty
      ? darkMode ? 'border-gray-600 border-dashed' : 'border-gray-300 border-dashed'
      : darkMode ? 'border-gray-700' : 'border-gray-200'
    }
    border-2 transition-all duration-200
  `;

  return (
    <>
      <div
        className={`
          relative rounded-lg p-2 border cursor-pointer
          ${isEmpty
            ? darkMode
              ? 'bg-gray-800/50 border-gray-600 border-dashed hover:bg-gray-700/50 hover:border-gray-500'
              : 'bg-white border-gray-300 border-dashed hover:bg-indigo-50/40 hover:border-indigo-400'
            : darkMode
              ? 'bg-gray-900 border-gray-700 shadow-sm hover:border-gray-600'
              : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'
          }
          transition-all duration-200
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (!photo) {
            setShowActionModal(true);
          } else {
            onZoom(photo.base64, title, () => setShowActionModal(true), onRemove);
          }
        }}
      >
        {/* Label */}
        <div className={`flex items-center justify-center gap-0.5 mb-1.5 text-[10px] font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span className="truncate text-center">{label}</span>
          {required && <span className="flex-shrink-0 text-red-400 font-bold">*</span>}
        </div>

        {/* Photo area container */}
        <div className={shape === 'circle' ? 'w-32 mx-auto' : 'w-full'}>
          <div className={photoClasses}>
            {photo ? (
              <img
                src={photo.base64}
                alt={title}
                // Stretched into the slot. A free selection rarely matches the slot's ratio, and
                // the alternatives both lose: cover CUTS the photo, contain strands it in bars.
                className="absolute inset-0 w-full h-full object-fill"
              />
            ) : (
              <div className={`absolute inset-0 flex items-center justify-center ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`}>
                <div className="relative">
                  <Icon className={`w-7 h-7 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  {badge && (
                    <span className={`absolute -bottom-1 -right-1.5 text-[9px] font-black leading-none w-4 h-4 flex items-center justify-center rounded-full ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-400 text-white'}`}>
                      {badge}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Hover overlay */}
            {isHovered && (
              <div className={`absolute inset-0 flex items-center justify-center z-10 transition-opacity duration-150 ${
                isEmpty
                  ? darkMode ? 'bg-indigo-600/70' : 'bg-indigo-500/75'
                  : 'bg-black/45'
              }`}>
                {isEmpty ? (
                  <Icon className="w-7 h-7 text-white/90" />
                ) : (
                  <p className="text-white/90 font-medium text-[10px] text-center leading-relaxed px-1">
                    Ampliar
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action selection modal */}
      <PhotoActionModal
        open={showActionModal}
        onClose={() => setShowActionModal(false)}
        onUpload={onUpload}
        onWebcam={onWebcam}
        onIPCamera={onIPCamera}
        title={title}
        darkMode={darkMode}
      />
    </>
  );
};

export default PhotoUploadArea;
